import { useCallback, useEffect, useState } from "react";
import {
  defaultFilters,
  type CatalogFilters,
} from "../lib/filters";
import type { CapabilityStrengths } from "../types";
import { StatusDot } from "./ui/StatusDot";
import { MIXED_MODALITY_COMPARE_MSG } from "../lib/compareModality";

export interface IntentChip {
  key: string;
  label: string;
}

interface IntentApiResponse {
  ok?: boolean;
  query?: string;
  filters?: Partial<CatalogFilters> & Record<string, unknown>;
  compareModelIds?: string[];
  nameQuery?: string;
  modelIds?: string[];
  pendingProvider?: { id: string; name: string } | null;
  candidates?: { id: string; name: string; score: number }[];
  explanation?: string;
  confidence?: number;
  usedJev?: boolean;
  usage?: { inputTokens?: number; outputTokens?: number };
  chips?: IntentChip[];
  backend?: string;
  error?: string;
}

/** $0.042 per 1M input tokens (TypeSafe Jev). */
const JEV_INPUT_USD_PER_M = 0.042;
const INTENT_ENGINE_KEY = "modelmeter.intentEngine";

export type IntentEngineId = "jev" | "luna" | "haiku" | "deepflash";

const INTENT_ENGINES: {
  id: IntentEngineId;
  label: string;
  live: boolean;
}[] = [
  { id: "jev", label: "Jev", live: true },
  { id: "luna", label: "Luna", live: false },
  { id: "haiku", label: "Haiku", live: false },
  { id: "deepflash", label: "Deep Flash", live: false },
];

/** Low-think approx rates ($/1M). Output estimate: max(400, round(input * 0.5)). */
const LUNA_IN_USD_PER_M = 0.2;
const LUNA_OUT_USD_PER_M = 1.2;
const HAIKU_IN_USD_PER_M = 1.0;
const HAIKU_OUT_USD_PER_M = 5.0;
const DEEPFLASH_IN_USD_PER_M = 0.15;
const DEEPFLASH_OUT_USD_PER_M = 0.6;

function isIntentEngineId(v: string): v is IntentEngineId {
  return INTENT_ENGINES.some((e) => e.id === v);
}

function readStoredEngine(): IntentEngineId {
  try {
    const raw = localStorage.getItem(INTENT_ENGINE_KEY);
    if (raw && isIntentEngineId(raw)) return raw;
  } catch {
    /* private mode / SSR */
  }
  return "jev";
}

function engineLabel(id: IntentEngineId): string {
  return INTENT_ENGINES.find((e) => e.id === id)?.label ?? id;
}

function backendShort(backend?: string): string {
  if (!backend) return "heuristic";
  return backend
    .replace(/^intent-/, "")
    .replace(/^jev-/, "");
}

function formatUsd(usd: number): string {
  if (usd < 0.000001) return "≈ <$0.000001";
  if (usd < 0.01) return `≈ $${usd.toFixed(6)}`;
  return `≈ $${usd.toFixed(4)}`;
}

function formatJevCost(inputTokens: number): string {
  return formatUsd((inputTokens / 1_000_000) * JEV_INPUT_USD_PER_M);
}

function lowThinkOutTokens(inputTokens: number): number {
  return Math.max(400, Math.round(inputTokens * 0.5));
}

function engineLowThinkCost(
  inputTokens: number,
  inPerM: number,
  outPerM: number,
): number {
  const out = lowThinkOutTokens(inputTokens);
  return (inputTokens / 1_000_000) * inPerM + (out / 1_000_000) * outPerM;
}

/** This-apply cost line for LLM picker (approx engines not called). */
function formatApplyCostCompare(inputTokens: number): string {
  const jev = formatJevCost(inputTokens);
  const luna = formatUsd(
    engineLowThinkCost(inputTokens, LUNA_IN_USD_PER_M, LUNA_OUT_USD_PER_M),
  );
  const haiku = formatUsd(
    engineLowThinkCost(inputTokens, HAIKU_IN_USD_PER_M, HAIKU_OUT_USD_PER_M),
  );
  const deep = formatUsd(
    engineLowThinkCost(
      inputTokens,
      DEEPFLASH_IN_USD_PER_M,
      DEEPFLASH_OUT_USD_PER_M,
    ),
  );
  return (
    `This apply (~${Math.round(inputTokens)} in): ` +
    `Jev ${jev} · Luna low-think ${luna} · Haiku low-think ${haiku} · ` +
    `Deep Flash low-think ${deep}`
  );
}

function formatLastIntentStatus(
  data: IntentApiResponse,
  selected: IntentEngineId,
): string {
  const approx =
    selected !== "jev"
      ? ` · approx · would use ${engineLabel(selected)}`
      : "";
  if (data.usedJev) {
    const tokens = data.usage?.inputTokens;
    const tokenPart =
      tokens != null && Number.isFinite(tokens)
        ? ` · ~${Math.round(tokens)} tokens · ${formatJevCost(tokens)}`
        : "";
    return `last intent: used Jev${tokenPart}${approx}`;
  }
  return `last intent: no Jev · ${backendShort(data.backend)}${approx}`;
}

const PLACEHOLDER =
  "Compare GPT Image 2 and Seedance 2.5 — or: only Flux / add provider Foo";

const STRENGTH_SET = new Set<string>([
  "edit",
  "lora",
  "textToImage",
  "imageToImage",
  "textToVideo",
  "imageToVideo",
  "audioInVideo",
  "characterReference",
  "controlnet",
  "upscale",
  "inpaint",
  "outpaint",
  "styleReference",
  "moodboard",
  "training",
]);

function isStrength(v: string): v is keyof CapabilityStrengths {
  return STRENGTH_SET.has(v);
}

/** Normalize API filter patch onto CatalogFilters. */
export function applyIntentFilters(
  base: CatalogFilters,
  patch: Partial<CatalogFilters> & Record<string, unknown>,
): CatalogFilters {
  const next: CatalogFilters = { ...base };
  if (typeof patch.q === "string") next.q = patch.q;
  if (
    patch.modality === "image" ||
    patch.modality === "video" ||
    patch.modality === "both" ||
    patch.modality === "all"
  ) {
    next.modality = patch.modality;
  }
  if (typeof patch.providerId === "string") next.providerId = patch.providerId;
  if (typeof patch.capability === "string") next.capability = patch.capability;
  if (typeof patch.strength === "string") {
    if (patch.strength === "all") next.strength = "all";
    else if (isStrength(patch.strength)) next.strength = patch.strength;
  }
  if (
    patch.qualityRank === "sota" ||
    patch.qualityRank === "good" ||
    patch.qualityRank === "usable" ||
    patch.qualityRank === "all"
  ) {
    next.qualityRank = patch.qualityRank;
  }
  if (
    patch.confidence === "verified" ||
    patch.confidence === "estimated" ||
    patch.confidence === "unknown" ||
    patch.confidence === "all"
  ) {
    next.confidence = patch.confidence;
  }
  if ("maxPrice" in patch) {
    const v = patch.maxPrice;
    if (v == null) next.maxPrice = null;
    else {
      const n = typeof v === "number" ? v : Number(v);
      next.maxPrice = Number.isFinite(n) ? n : null;
    }
  }
  if (typeof patch.status === "string") next.status = patch.status;
  if (typeof patch.sort === "string") {
    next.sort = patch.sort as CatalogFilters["sort"];
  }
  if (typeof patch.nameQuery === "string") next.nameQuery = patch.nameQuery;
  if ("modelIds" in patch) {
    const ids = patch.modelIds;
    if (ids == null) next.modelIds = null;
    else if (Array.isArray(ids)) {
      next.modelIds = ids.filter((x): x is string => typeof x === "string");
    }
  }
  return next;
}

export function activeFilterChips(filters: CatalogFilters): IntentChip[] {
  const chips: IntentChip[] = [];
  if (filters.modality !== "all") {
    chips.push({ key: "modality", label: `modality:${filters.modality}` });
  }
  if (filters.qualityRank !== "all") {
    chips.push({ key: "qualityRank", label: `quality:${filters.qualityRank}` });
  }
  if (filters.strength !== "all") {
    chips.push({ key: "strength", label: `strength:${filters.strength}` });
  }
  if (filters.capability !== "all") {
    chips.push({ key: "capability", label: `cap:${filters.capability}` });
  }
  if (filters.maxPrice != null) {
    chips.push({ key: "maxPrice", label: `≤$${filters.maxPrice}` });
  }
  if (filters.confidence !== "all") {
    chips.push({ key: "confidence", label: `conf:${filters.confidence}` });
  }
  if (filters.status !== "all") {
    chips.push({ key: "status", label: `status:${filters.status}` });
  }
  if (filters.providerId !== "all") {
    chips.push({ key: "providerId", label: `provider:${filters.providerId}` });
  }
  if (filters.sort !== "name") {
    chips.push({ key: "sort", label: `sort:${filters.sort}` });
  }
  if (filters.q) chips.push({ key: "q", label: `q:${filters.q}` });
  if (filters.nameQuery) {
    chips.push({ key: "nameQuery", label: `name:${filters.nameQuery}` });
  }
  if (filters.modelIds?.length) {
    chips.push({ key: "modelIds", label: `models:${filters.modelIds.length}` });
  }
  return chips;
}

export function clearChipKey(
  filters: CatalogFilters,
  key: string,
): CatalogFilters {
  const next = { ...filters };
  switch (key) {
    case "modality":
      next.modality = "all";
      break;
    case "qualityRank":
      next.qualityRank = "all";
      break;
    case "strength":
      next.strength = "all";
      break;
    case "capability":
      next.capability = "all";
      break;
    case "maxPrice":
      next.maxPrice = null;
      break;
    case "confidence":
      next.confidence = "all";
      break;
    case "status":
      next.status = "all";
      break;
    case "providerId":
      next.providerId = "all";
      break;
    case "sort":
      next.sort = "name";
      break;
    case "q":
      next.q = "";
      break;
    case "nameQuery":
      next.nameQuery = "";
      break;
    case "modelIds":
      next.modelIds = null;
      break;
    default:
      break;
  }
  return next;
}

export function IntentSearch({
  filters,
  resultCount,
  onFiltersChange,
  onCompare,
  onReset,
}: {
  filters: CatalogFilters;
  resultCount: number;
  onFiltersChange: (next: CatalogFilters) => void;
  onCompare: (modelIds: string[]) => void;
  onReset: () => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<number | null>(null);
  const [usedJev, setUsedJev] = useState(false);
  const [lastStatus, setLastStatus] = useState<string | null>(null);
  /** Last intent input tokens for this-apply cost compare; null before first apply. */
  const [lastInputTokens, setLastInputTokens] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [engineOpen, setEngineOpen] = useState(false);
  const [engine, setEngine] = useState<IntentEngineId>("jev");

  useEffect(() => {
    setEngine(readStoredEngine());
  }, []);

  const selectEngine = useCallback((id: IntentEngineId) => {
    setEngine(id);
    try {
      localStorage.setItem(INTENT_ENGINE_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const chips = activeFilterChips(filters);
  const engineMeta = INTENT_ENGINES.find((e) => e.id === engine) ?? INTENT_ENGINES[0];

  const run = useCallback(async () => {
    const query = text.trim();
    if (!query) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });
      const data = (await res.json()) as IntentApiResponse;
      if (!res.ok) {
        setError(data.error ?? "Intent parse failed");
        return;
      }
      const ids = data.compareModelIds ?? [];
      if (ids.length >= 2) {
        // Compare wins — do not leave taxonomy chips from a bad prior map
        onFiltersChange({ ...defaultFilters });
        onCompare(ids);
      } else {
        const patch = { ...(data.filters ?? {}) } as Partial<CatalogFilters> &
          Record<string, unknown>;
        if (typeof data.nameQuery === "string") patch.nameQuery = data.nameQuery;
        if (Array.isArray(data.modelIds)) patch.modelIds = data.modelIds;
        const next = applyIntentFilters(defaultFilters, patch);
        onFiltersChange(next);
      }
      setExplanation(data.explanation ?? null);
      setConfidence(data.confidence ?? null);
      setUsedJev(Boolean(data.usedJev));
      setLastStatus(formatLastIntentStatus(data, engine));
      {
        const t = data.usage?.inputTokens;
        setLastInputTokens(
          t != null && Number.isFinite(t) ? t : 1500,
        );
      }
      if (data.error) setError(data.error);
      // Modality mismatch: show explanation as error note; never open compare.
      else if (
        ids.length < 2 &&
        (data.explanation === MIXED_MODALITY_COMPARE_MSG ||
          (typeof data.explanation === "string" &&
            /image and video/i.test(data.explanation)))
      ) {
        setError(data.explanation);
      }
      if (data.pendingProvider) {
        setExplanation(
          (data.explanation ?? "") +
            ` Provider "${data.pendingProvider.name}" queued for /admin approval.`,
        );
      }
    } catch {
      setError("Intent endpoint unavailable");
    } finally {
      setBusy(false);
    }
  }, [text, engine, onFiltersChange, onCompare]);

  return (
    <section className="intent-band" aria-label="Natural language catalog intent">
      <div className="intent-band-head">
        <p className="eyebrow" style={{ marginBottom: 0 }}>
          02 / INTENT
        </p>
        <StatusDot
          kind={busy ? "running" : usedJev ? "live" : "synced"}
          label={
            busy
              ? "PARSING"
              : usedJev
                ? "JEV · SAVED CATEGORIZATION"
                : "READY"
          }
        />
      </div>

      <div className="intent-engine">
        <button
          type="button"
          className="intent-engine-toggle"
          aria-expanded={engineOpen}
          onClick={() => setEngineOpen((o) => !o)}
        >
          <span className="intent-engine-toggle-label">LLM</span>
          <span className="intent-engine-toggle-value mono">
            {engineMeta.label}
            {engineMeta.live ? " · LIVE" : " · approx"}
          </span>
          <span className="intent-engine-chevron" aria-hidden>
            {engineOpen ? "▾" : "▸"}
          </span>
        </button>
        {engineOpen && (
          <div className="intent-engine-panel">
            <div
              className="segmented intent-engine-segmented"
              role="radiogroup"
              aria-label="Intent LLM"
            >
              {INTENT_ENGINES.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  role="radio"
                  aria-checked={engine === e.id}
                  className={engine === e.id ? "active" : undefined}
                  onClick={() => selectEngine(e.id)}
                >
                  {e.label}
                  {e.live ? (
                    <span className="intent-engine-live">LIVE</span>
                  ) : null}
                </button>
              ))}
            </div>
            <p className="intent-engine-costs muted mono">
              {lastInputTokens == null
                ? "Run Apply to estimate"
                : formatApplyCostCompare(lastInputTokens)}
            </p>
            {lastInputTokens != null && (
              <p className="intent-engine-costs muted mono">
                low-think ≈ ~0.5× input as billed output · approximate, not billed
              </p>
            )}
            {engine !== "jev" && (
              <p className="intent-engine-approx muted mono">
                Approximate — not wired; discovery still uses Jev
              </p>
            )}
          </div>
        )}
      </div>

      <form
        className="intent-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <label className="sr-only" htmlFor="catalog-intent">
          Describe models you want
        </label>
        <input
          id="catalog-intent"
          className="intent-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={PLACEHOLDER}
          disabled={busy}
          autoComplete="off"
        />
        <button type="submit" className="btn sm" disabled={busy || !text.trim()}>
          {busy ? "Mapping…" : "Apply"}
        </button>
      </form>

      {lastStatus && (
        <p className="intent-status muted mono" aria-live="polite">
          {lastStatus}
        </p>
      )}

      {chips.length > 0 && (
        <div className="intent-chips" role="list" aria-label="Active filters">
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              className="intent-chip"
              role="listitem"
              onClick={() => onFiltersChange(clearChipKey(filters, c.key))}
              title="Remove filter"
            >
              <span>{c.label}</span>
              <span aria-hidden>×</span>
            </button>
          ))}
          <button type="button" className="btn ghost sm" onClick={onReset}>
            Clear
          </button>
        </div>
      )}

      <div className="intent-meta">
        <span>
          {explanation ??
            "Describe what you need — Jev maps it onto saved quality / strength categorization."}
          {confidence != null
            ? ` · ${Math.round(Math.min(1, Math.max(0, confidence)) * 100)}%`
            : ""}
        </span>
        <span>
          {resultCount} matches
          {busy ? " · syncing" : ""}
        </span>
      </div>
      {error && (
        <p className="intent-error muted" style={{ margin: "0.35rem 0 0" }}>
          Note: {error}
        </p>
      )}
    </section>
  );
}
